import { Box, Button as MuiButton } from '@mui/material';
import { useMemo } from 'react';
import { componentSizes } from '../../theme/componentSizes';
import { r, s, spacing } from '../../theme/tokens';
import { CircularProgress } from './CircularProgress';
import { Icon } from './Icon';
import type { ButtonProps as MuiButtonProps } from '@mui/material/Button';
import type { ElementType, ReactElement, ReactNode } from 'react';

type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'ghost'
  | 'destructive'
  | 'contained'
  | 'outlined'
  | 'text';
type ButtonSize = 'sm' | 'md' | 'lg' | 'small' | 'medium' | 'large';
type ButtonVisualSize = 'sm' | 'md' | 'lg';

export type ButtonProps<C extends ElementType = 'button'> = Omit<
  MuiButtonProps<C>,
  'variant' | 'size' | 'startIcon' | 'endIcon' | 'loading'
> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  fullWidthOnMobile?: boolean;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  to?: string;
};

const resolveLoadingAriaLabel = (children: ReactNode, ariaLabel: string | undefined): string => {
  if (typeof ariaLabel === 'string' && ariaLabel.trim().length > 0) {
    return ariaLabel;
  }
  if (typeof children === 'string' || typeof children === 'number') {
    return String(children);
  }
  return 'Loading';
};

const sizeStyles: Record<
  ButtonVisualSize,
  { paddingY: string; paddingX: string; fontSize: number }
> = {
  sm: { paddingY: s('xs'), paddingX: s('md'), fontSize: componentSizes.buttons.fontSize },
  md: { paddingY: s('sm'), paddingX: s('lg'), fontSize: componentSizes.buttons.fontSize },
  lg: { paddingY: s('md'), paddingX: s('xl'), fontSize: componentSizes.buttons.fontSize },
};

export const Button = <C extends ElementType = 'button'>({
  variant = 'primary',
  size = 'md',
  loading = false,
  fullWidthOnMobile = true,
  'aria-label': ariaLabel,
  color,
  disabled,
  children,
  startIcon,
  endIcon,
  sx,
  ...rest
}: ButtonProps<C>): ReactElement => {
  const mappedVariant = useMemo(() => {
    if (variant === 'secondary') return 'outlined';
    if (variant === 'ghost') return 'text';
    if (variant === 'destructive') return 'outlined';
    if (variant === 'primary') return 'contained';
    return variant;
  }, [variant]) as MuiButtonProps['variant'];
  const mappedColor: MuiButtonProps['color'] = variant === 'destructive' ? 'error' : color;

  const resolvedSize: ButtonVisualSize =
    size === 'small' ? 'sm' : size === 'large' ? 'lg' : size === 'medium' ? 'md' : size;
  const style = sizeStyles[resolvedSize];
  const isSecondary = mappedVariant === 'outlined';
  const isDestructive = variant === 'destructive';
  const isErrorOutlined = mappedVariant === 'outlined' && mappedColor === 'error';
  const isDisabled = disabled === true || loading;

  const iconSize = resolvedSize;

  const mergedSx = [
    {
      borderRadius: r('sm'),
      textTransform: 'none',
      fontWeight: 600,
      fontSize: style.fontSize,
      height: componentSizes.buttons.height,
      minHeight: componentSizes.buttons.height,
      paddingY: style.paddingY,
      paddingX: style.paddingX,
      position: 'relative',
      width: fullWidthOnMobile ? { xs: '100%', sm: 'auto' } : 'auto',
      minWidth: 'auto',
      ...(loading
        ? {
            '& .MuiButton-startIcon, & .MuiButton-endIcon': {
              visibility: 'hidden',
            },
          }
        : {}),
      borderColor:
        isSecondary && !isDestructive
          ? isErrorOutlined
            ? 'error.main'
            : 'primary.main'
          : undefined,
      '&:focus-visible': { outline: '2px solid currentColor', outlineOffset: s('xs') },
    },
    isErrorOutlined && !isDestructive
      ? {
          '&&': {
            borderColor: 'error.main',
            color: 'error.main',
          },
          '&&:hover': {
            borderColor: 'error.main',
            color: 'error.main',
            backgroundColor: 'rgba(239, 68, 68, 0.08)',
          },
        }
      : undefined,
    isDestructive
      ? {
          '&&': {
            borderColor: 'error.main',
            color: 'error.main',
          },
          '&&:hover': {
            borderColor: 'error.main',
            color: 'error.main',
            backgroundColor: 'rgba(239, 68, 68, 0.08)',
          },
        }
      : undefined,
    ...(sx ? (Array.isArray(sx) ? sx : [sx]) : []),
  ].filter(Boolean) as MuiButtonProps<C>['sx'];

  const loadingAriaLabel = resolveLoadingAriaLabel(children, ariaLabel);

  return (
    <MuiButton
      variant={mappedVariant}
      color={mappedColor}
      aria-busy={loading || undefined}
      aria-label={loading ? loadingAriaLabel : ariaLabel}
      disabled={isDisabled}
      startIcon={startIcon ? <Icon size={iconSize}>{startIcon}</Icon> : undefined}
      endIcon={endIcon ? <Icon size={iconSize}>{endIcon}</Icon> : undefined}
      sx={mergedSx}
      {...rest}
    >
      <Box
        component="span"
        sx={{
          visibility: loading ? 'hidden' : 'visible',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
        }}
      >
        {children}
      </Box>
      {loading ? (
        <Box
          component="span"
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
          }}
        >
          <CircularProgress size={spacing.lg} color="inherit" />
        </Box>
      ) : null}
    </MuiButton>
  );
};
